import { RaisePropertyDefinitionMissingError } from '../services';
import { ApiUrl, ECatalogType } from '../data/Constants';
import AxiosClient from './AxiosClient';
import { getHeaders } from './HttpUtil';
import { AsyncThunkConfig, GetThunkAPI } from '@reduxjs/toolkit';
import { getPageDetails } from '../store/Helpers/LandingHelper';

import type {
  IAuthorizationRequest,
  IModelRequest,
  IProductCatalogRequest,
  IPropertyAssociationRequest,
  IProductDataRequest,
} from '../../types/IApiTypes';
import type { IPackageDetails } from '../../types/ITypes';

export const ProductApi = {
  productCatalog: async (payload: IProductCatalogRequest) => {
    const { token, page, limit, searchKey, agCodes, channelCodes } = payload;
    let parmas = { Page: page, Limit: limit, catalogType: ECatalogType.Release };
    if (searchKey) {
      const search = { search: searchKey };
      parmas = { ...search, ...parmas };
    }

    try {
      const response = await AxiosClient.post(
        '/product/v1/catalog',
        {
          agCodes: agCodes ?? [],
          channels: channelCodes ?? [],
        },
        { params: parmas, headers: getHeaders(token)?.headers }
      );
      return {
        productCatalog: response.data || [],
        pageDetails: getPageDetails(response, page, limit),
      };
    } catch {
      return null;
    }
  },

  getProductImages: (action: IModelRequest & IPackageDetails) => {
    return AxiosClient.post(
      ApiUrl.ImagesApi,
      {
        packageType: action.packageType,
        packageCode: action.packageCode,
        productCode: action.modelId,
      },
      { headers: { ...getHeaders(action.token).headers, skipLoader: true } }
    )
      .then((response) => {
        return {
          productImages: response.data,
          modelId: action.modelId,
        };
      })
      .catch(() => {
        return {
          modelId: action.modelId,
        };
      });
  },

  productFilter: async (payload: IAuthorizationRequest) => {
    try {
      const response = await AxiosClient.get('/product/v1/filter/read', {
        params: { catalogType: ECatalogType.Release },
        headers: getHeaders(payload.token)?.headers,
      });
      return {
        productFilterOptions: response.data,
      };
    } catch {
      return [];
    }
  },

  //Get Property Associations from DB using DIS API
  getPropertyAssociations: async (
    payload: IPropertyAssociationRequest,
    thunkAPI: GetThunkAPI<AsyncThunkConfig>
  ) => {
    const req = {
      packageCode: payload.packageCode,
      productCode: payload.productCode,
      packageType: payload.packageType,
    };
    try {
      const response = await AxiosClient.post(
        '/product/v1/propertyassociation/read',
        req,
        getHeaders(payload.token)
      );

      //check property definition is available on the response if not show the error dialog
      if (payload.packageCode === payload.productCode) {
        RaisePropertyDefinitionMissingError(response.data.definitions, thunkAPI);
      }

      return {
        propertiesAssociation: response.data.propertyAssociations,
        propertyDefinition: response.data.definitions ?? payload.propertyDefiniton,
        modelId: payload.productCode,
        packageId: payload.packageCode,
      };
    } catch {
      //check property definition is available on the response if not show the error dialog
      if (payload.packageCode === payload.productCode) {
        RaisePropertyDefinitionMissingError([], thunkAPI);
        return null;
      }
    }
  },

  //Get the submodel count variable data
  getProductData: async (payload: IProductDataRequest) => {
    const req = {
      productCode: payload.productCode,
      packageCode: payload.packageCode,
      packageType: payload.packageType,
    };
    try {
      const response = await AxiosClient.post(
        '/product/v1/data/read',
        req,
        getHeaders(payload.token)
      );
      return {
        subModelData: response.data,
        modelId: payload.productCode,
      };
    } catch (err) {
      throw new Error(err instanceof Error ? err.message : 'Failed to get submodel data', {
        cause: err,
      });
    }
  },

  // internal Product portfolio API's
  internalProductCatalog: async (payload: IProductCatalogRequest) => {
    const { token, page, limit, searchKey, agCodes, channelCodes } = payload;
    let parmas = { Page: page, Limit: limit, catalogType: ECatalogType.Internal };
    if (searchKey) {
      const search = { search: searchKey };
      parmas = { ...search, ...parmas };
    }

    try {
      const response = await AxiosClient.post(
        '/product/v1/catalog',
        {
          agCodes: agCodes ?? [],
          channels: channelCodes ?? [],
        },
        { params: parmas, headers: getHeaders(token)?.headers }
      );
      return {
        productCatalog: response.data || [],
        pageDetails: getPageDetails(response, page, limit),
      };
    } catch {
      return null;
    }
  },

  internalProductFilter: async (payload: IAuthorizationRequest) => {
    try {
      const response = await AxiosClient.get('/product/v1/filter/read', {
        params: { catalogType: ECatalogType.Internal },
        headers: getHeaders(payload.token)?.headers,
      });
      return { productFilterOptions: response.data };
    } catch {
      return [];
    }
  },
};
